<template lang="pug">
    v-btn.dd-search-toggle(
        v-if="collapsed"
        icon
        :aria-label="label"
        @click="expand")
        v-icon search
    v-text-field(
        v-else
        ref="field"
        v-model="search"
        hide-details="auto"
        :placeholder="label"
        :aria-label="label"
        :readonly="busy"
        prepend-inner-icon="search"
        class="search-field"
        background-color="white"
        outlined
        dense
        @blur="collapse")
        span(
            slot="append"
            v-if="search !== ''"
            class="clear-link"
            @mousedown.prevent
            @click.prevent="search = ''")
            v-icon clear
</template>

<script>
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'

export default {
    props: {
        value: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            default: () => {},
        },
        busy: {
            type: Boolean,
        },
        // an icon button that opens into the field and closes again when left empty
        // (merchant portal Video library search, tasks#211)
        collapsible: {
            type: Boolean,
        },
        // ms before a typed value is emitted; 0 inside a filter pill, which applies on its button
        debounce: {
            type: Number,
            default: 800,
        },
        autofocus: {
            type: Boolean,
        },
    },
    data() {
        return {
            search: '',
            expanded: false,
        }
    },
    computed: {
        key() {
            return this.field?.key || '_search'
        },
        type() {
            return this.field?.type
        },
        // the list search gets its text from the parent's `label` attribute (TableFilters), a quick
        // filter from its field; without it the list search had no placeholder at all
        label() {
            return this.field?.label || this.$attrs.label
        },
        collapsed() {
            return this.collapsible && !this.expanded && this.search === ''
        },
    },
    watch: {
        search() {
            this.emitSearch()
        },
        value: {
            immediate: true,
            deep: true,
            handler(value) {
                const incoming = this.type
                    ? value?.[this.key]?.[this.type] || ''
                    : value?.[this.key] || ''

                // The parent echoes back the trimmed value we emitted; do not
                // overwrite the text the user is still typing with it.
                if (incoming !== (this.search || '').trim())
                    this.search = incoming
            },
        },
    },
    created() {
        this.emitSearch = this.debounce
            ? debounce(this.applySearch, this.debounce)
            : this.applySearch
    },
    mounted() {
        if (this.autofocus) this.focus()
    },
    methods: {
        applySearch() {
            const filters = cloneDeep(this.value)
            // The user may still be typing (trailing space before the next
            // word); the request goes out trimmed, the field keeps its text.
            const searchValue = (this.search || '').trim()

            if (searchValue === '') {
                if (this.type) {
                    if (filters[this.key]) {
                        delete filters[this.key][this.type]
                        if (!Object.keys(filters[this.key]).length)
                            delete filters[this.key]
                    }
                } else {
                    delete filters[this.key]
                }
            } else {
                if (this.type) {
                    if (!filters[this.key]) filters[this.key] = {}
                    filters[this.key][this.type] = searchValue
                } else filters[this.key] = searchValue
            }

            this.$emit('input', filters)
        },
        focus() {
            this.$nextTick(() => this.$refs.field?.focus())
        },
        expand() {
            this.expanded = true
            this.focus()
        },
        collapse() {
            if (this.search === '') this.expanded = false
        },
    },
}
</script>

<style scoped>
.clear-link {
    position: absolute;
    right: 4px;
    cursor: pointer;
}
</style>
